import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut } from 'lucide-react';
import DashboardTab from './tabs/DashboardTab.jsx';
import AnimalsTab from './tabs/AnimalsTab.jsx';
import VaccinationsTab from './tabs/VaccinationsTab.jsx';
import ProductionTab from './tabs/ProductionTab.jsx';
import ExpenditureTab from './tabs/ExpenditureTab.jsx';
import FinanceTab from './tabs/FinanceTab.jsx';
import CustomersTab from './tabs/CustomersTab.jsx';
import SettingsTab from './tabs/SettingsTab';
import ReportsTab from './tabs/ReportsTab';

const DairyDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
const [showNotifications, setShowNotifications] = useState(false);
const [notifications, setNotifications] = useState([]);
const [notifLoading, setNotifLoading] = useState(false);


  const isBusinessman = user?.role === 'businessman';

  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', adminOnly: false },
    { id: 'animals', label: 'Animals', icon: '🐄', adminOnly: false },
    { id: 'vaccinations', label: 'Medication', icon: '💉', adminOnly: false },
    { id: 'production', label: 'Production', icon: '🥛', adminOnly: false },
    { id: 'customers', label: 'Customers', icon: '👥', adminOnly: false },
    { id: 'expenditure', label: 'Expenditure', icon: '💸', adminOnly: false },
    { id: 'finance', label: 'Sale/Finance', icon: '💰', adminOnly: false },
    { id: 'settings', label: 'Settings', icon: '⚙️', adminOnly: false },
    { id: 'reports', label: 'Reports', icon: '📋', adminOnly: true },
  ];

  const fetchNotifications = async () => {
  setNotifLoading(true);
  try {
    const [animalsRes, customersRes, vendorsRes, medicinesRes] = await Promise.all([
      fetch(`https://sohaibenterprises.cloud/api/animals`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()),
      fetch(`https://sohaibenterprises.cloud/api/customers`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()),
      fetch(`https://sohaibenterprises.cloud/api/vendors`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()),
      fetch(`https://sohaibenterprises.cloud/api/medicines`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.json()),
    ]);

    const notifs = [];
    const today = new Date();

    // Sick animals
    (animalsRes.data || []).filter(a => a.health_status === 'Sick' || a.health_status === 'Treatment').forEach(a => {
      notifs.push({ type: 'animal', icon: '🐄', color: '#e74c3c', message: `${a.name || a.tag_number} needs attention (${a.health_status})`, tab: 'animals' });
    });

    // Checkup due animals
    (animalsRes.data || []).filter(a => a.health_status === 'Checkup Due').forEach(a => {
      notifs.push({ type: 'animal', icon: '🩺', color: '#e67e22', message: `${a.name || a.tag_number} has checkup due`, tab: 'animals' });
    });

    // Customers with outstanding balance
    (customersRes.data || []).filter(c => parseFloat(c.outstanding_balance || 0) > 0).forEach(c => {
      notifs.push({ type: 'customer', icon: '💰', color: '#3498db', message: `${c.name} owes Rs ${Math.round(parseFloat(c.outstanding_balance)).toLocaleString()}`, tab: 'customers' });
    });

    // Vendors with outstanding balance
    (vendorsRes.data || []).filter(v => parseFloat(v.outstanding_balance || 0) > 0).forEach(v => {
      notifs.push({ type: 'vendor', icon: '🏪', color: '#8e44ad', message: `Pending payment to ${v.name}: Rs ${Math.round(parseFloat(v.outstanding_balance)).toLocaleString()}`, tab: 'expenditure' });
    });

    // Pregnant animals with delivery in 7 days
    (animalsRes.data || []).filter(a => {
      if (!a.expected_delivery_date) return false;
      const delivery = new Date(a.expected_delivery_date);
      const daysLeft = Math.ceil((delivery - today) / (1000 * 60 * 60 * 24));
      return daysLeft <= 7 && daysLeft >= 0;
    }).forEach(a => {
      const daysLeft = Math.ceil((new Date(a.expected_delivery_date) - today) / (1000 * 60 * 60 * 24));
      notifs.push({ type: 'pregnancy', icon: '🤰', color: '#e91e63', message: `${a.name || a.tag_number} expected to deliver in ${daysLeft} day(s)`, tab: 'animals' });
    });

    // Medicines expiring soon (within 30 days)
    (medicinesRes.data || []).filter(m => {
      if (!m.expiry_date) return false;
      const expiry = new Date(m.expiry_date);
      const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
      return daysLeft <= 30 && daysLeft >= 0;
    }).forEach(m => {
      const daysLeft = Math.ceil((new Date(m.expiry_date) - today) / (1000 * 60 * 60 * 24));
      notifs.push({ type: 'medicine', icon: '💊', color: '#e74c3c', message: `${m.medicine_name} expires in ${daysLeft} days`, tab: 'vaccinations' });
    });

    setNotifications(notifs);
  } catch (error) {
    console.error('Error fetching notifications:', error);
  } finally {
    setNotifLoading(false);
  }
};

  // Filter tabs based on role
  const tabs = allTabs.filter(tab => !tab.adminOnly || isBusinessman);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab setActiveTab={setActiveTab} />;
      case 'animals': return <AnimalsTab />;
      case 'vaccinations': return <VaccinationsTab />;
      case 'production': return <ProductionTab />;
      case 'customers': return <CustomersTab />;
      case 'expenditure': return <ExpenditureTab />;
      case 'finance': return <FinanceTab />;
      case 'settings': return <SettingsTab />;
      case 'reports': return isBusinessman ? <ReportsTab /> : <DashboardTab />;
      default: return <DashboardTab />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" }}>
      <div style={{
        background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
        color: 'white',
        padding: '20px 40px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {/* Left Side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
            {isBusinessman && (
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.3)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.2)'; }}
              >
                ← Back
              </button>
            )}
            <div>
              <h1 style={{ fontSize: '28px', margin: '0 0 4px 0', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '32px' }}>🥛</span>
                Dairy Farm Dashboard
              </h1>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                Livestock & Production Management System
              </p>
            </div>
          </div>

          {/* Right Side - Username + Logout + Notifications */}
          {/* Right Side - Notifications + Username + Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications) fetchNotifications(); }}
                style={{ padding: '10px 16px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', cursor: 'pointer', fontSize: '20px', position: 'relative' }}
              >
                🔔
                {notifications.length > 0 && (
                  <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#e74c3c', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {notifications.length > 9 ? '9+' : notifications.length}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div style={{ position: 'absolute', right: 0, top: '50px', width: '360px', background: 'white', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 1000, maxHeight: '480px', overflow: 'auto' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>🔔 Notifications</h3>
                    <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#95a5a6' }}>✕</button>
                  </div>
                  {notifLoading ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#95a5a6' }}>⏳ Loading...</div>
                  ) : notifications.length === 0 ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#95a5a6' }}>
                      <div style={{ fontSize: '36px', marginBottom: '8px' }}>✅</div>
                      <div style={{ fontSize: '14px' }}>All clear! No notifications.</div>
                    </div>
                  ) : (
                    <div>
                      {notifications.map((notif, i) => (
                        <div key={i} onClick={() => { setActiveTab(notif.tab); setShowNotifications(false); }}
                          style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', background: 'white' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ fontSize: '20px' }}>{notif.icon}</span>
                          <span style={{ fontSize: '13px', color: '#2c3e50', flex: 1 }}>{notif.message}</span>
                          <span style={{ fontSize: '11px', color: notif.color, fontWeight: '700', whiteSpace: 'nowrap' }}>→</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{
              padding: '10px 16px',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              👤 {user?.full_name || user?.username || 'User'}
            </div>
            <button
              onClick={logout}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.3)'; }}
              onMouseLeave={(e) => { e.target.style.background = 'rgba(255, 255, 255, 0.2)'; }}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          paddingTop: '16px',
          overflowX: 'auto',
          flexWrap: 'wrap'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                background: activeTab === tab.id ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                border: activeTab === tab.id ? '2px solid rgba(255, 255, 255, 0.5)' : '2px solid transparent',
                borderRadius: '10px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '700' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s',
                backdropFilter: activeTab === tab.id ? 'blur(10px)' : 'none',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { if (activeTab !== tab.id) e.target.style.background = 'rgba(255, 255, 255, 0.15)'; }}
              onMouseLeave={(e) => { if (activeTab !== tab.id) e.target.style.background = 'rgba(255, 255, 255, 0.1)'; }}
            >
              <span style={{ fontSize: '18px' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '40px' }}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DairyDashboard;